/**
 * CompletePaymentPage — gate shown after a user signs up via a paid-plan
 * CTA (e.g. clicked "Start $1 trial" on the pricing page). Until they
 * complete Stripe checkout (or explicitly opt out to the free tier), they
 * cannot enter the dashboard.
 *
 * Routed to from `ProtectedRoute` when `user.pending_signup_plan` is set
 * and the user is still on the `free` tier.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowRight, CreditCard, Loader2, Lock, Sparkles, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

export const CompletePaymentPage = () => {
  const { api, user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [tier, setTier] = useState(null);
  const [loadingTier, setLoadingTier] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [skipping, setSkipping] = useState(false);

  // Defensive: if the user shouldn't be here, bounce them.
  useEffect(() => {
    if (!user) return;
    if (!user.pending_signup_plan) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // Load the chosen tier's marketing info (price, name, promo, features).
  useEffect(() => {
    if (!user?.pending_signup_plan) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/subscription/tiers');
        const t = (r.data?.tiers || []).find((x) => x.id === user.pending_signup_plan);
        if (!cancelled) setTier(t || null);
      } catch (e) {
        if (!cancelled) toast.error('Could not load your selected plan');
      } finally {
        if (!cancelled) setLoadingTier(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.pending_signup_plan, api]);

  const goToStripe = async () => {
    if (!user?.pending_signup_plan) return;
    setRedirecting(true);
    try {
      const r = await api.post('/subscription/checkout', {
        tier_id: user.pending_signup_plan,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout');
      setRedirecting(false);
    }
  };

  const skipToFree = async () => {
    if (!window.confirm('Continue on the Free plan instead?\n\nYou can upgrade anytime from Settings → Subscription.')) {
      return;
    }
    setSkipping(true);
    try {
      await api.post('/subscription/skip-signup-plan');
      await refreshUser();
      toast.success("You're on the Free plan");
      navigate('/dashboard', { replace: true });
    } catch (e) {
      toast.error('Could not switch you to Free — please try again');
    } finally {
      setSkipping(false);
    }
  };

  if (loadingTier) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="complete-payment-loading">
        <Loader2 className="w-7 h-7 animate-spin text-indigo-600" />
      </div>
    );
  }

  const promo = tier?.promo_trial_enabled;
  const priceLabel = promo
    ? `$${Number(tier.promo_trial_price ?? 1).toFixed(2)} for ${tier.promo_trial_days ?? 5} days`
    : tier?.discount_active && tier?.discounted_price != null
      ? `$${Number(tier.discounted_price).toFixed(2)}/mo`
      : `$${Number(tier?.price || 0).toFixed(2)}/mo`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white flex items-center justify-center p-4" data-testid="complete-payment-page">
      <div className="w-full max-w-md">
        <Card className="border-indigo-200 shadow-xl">
          <CardHeader className="text-center space-y-2 pb-4">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-indigo-100 text-indigo-600 mx-auto">
              <Lock className="w-6 h-6" />
            </div>
            <CardTitle className="text-2xl tracking-tight">One more step</CardTitle>
            <CardDescription className="text-base">
              You picked the <b className="text-zinc-900">{tier?.name || user?.pending_signup_plan}</b> plan during signup. Complete payment to unlock your dashboard.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Plan summary card */}
            <div className="border border-zinc-200 rounded-lg p-4 bg-white" data-testid="complete-payment-plan-summary">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-zinc-900 flex items-center gap-1.5">
                    <Sparkles className="w-4 h-4 text-indigo-600" />
                    {tier?.name || 'Selected plan'}
                  </p>
                  {promo && (
                    <p className="text-[11px] text-emerald-700 font-semibold mt-0.5">
                      🎉 Promo: ${Number(tier.promo_trial_price ?? 1).toFixed(2)} for first {tier.promo_trial_days ?? 5} days
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-zinc-900">{priceLabel}</p>
                  {promo && (
                    <p className="text-[11px] text-zinc-500">then ${Number(tier.price).toFixed(0)}/mo</p>
                  )}
                </div>
              </div>
              {tier?.features?.slice(0, 4)?.length > 0 && (
                <ul className="mt-3 space-y-1 text-xs text-zinc-600">
                  {tier.features.slice(0, 4).map((f, i) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <span className="text-indigo-600 mt-0.5">✓</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Trust signals — keep the user confident they're being routed
                to a real Stripe-hosted checkout, not an in-app form. */}
            <div className="flex items-center justify-center gap-3 text-[11px] text-zinc-500">
              <span className="inline-flex items-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                Secure checkout
              </span>
              <span>·</span>
              <span>Powered by Stripe</span>
              <span>·</span>
              <span>Cancel anytime</span>
            </div>

            <Button
              className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 text-base"
              onClick={goToStripe}
              disabled={redirecting || skipping}
              data-testid="complete-payment-pay-btn"
            >
              {redirecting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CreditCard className="w-4 h-4 mr-2" />
              )}
              {promo
                ? `Pay $${Number(tier.promo_trial_price ?? 1).toFixed(2)} & unlock`
                : 'Continue to secure checkout'}
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>

            <Button
              variant="ghost"
              className="w-full text-zinc-600 hover:text-zinc-900"
              onClick={skipToFree}
              disabled={redirecting || skipping}
              data-testid="complete-payment-skip-btn"
            >
              {skipping && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              No thanks — continue on Free plan
            </Button>

            <div className="text-center pt-1 border-t border-zinc-100">
              <div className="text-[11px] text-zinc-400 flex items-center justify-center flex-wrap gap-1.5">
                <span>Signed in as</span>
                <Badge variant="outline" className="text-[10px] font-mono">{user?.email}</Badge>
                <button
                  type="button"
                  onClick={() => { logout(); navigate('/login'); }}
                  className="text-indigo-600 hover:underline"
                  data-testid="complete-payment-logout-link"
                >
                  Switch account
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CompletePaymentPage;
